import { getCountrySettings, getAvailableCountries, isCountrySupported } from './settings.js';

/**
 * Utilidades para manejo dinámico de configuraciones por país
 */

/**
 * Obtiene las configuraciones específicas para el país actual
 * @param {string} countryCode - Código de país específico (opcional)
 * @returns {object} Configuraciones del país
 */
export const getCurrentCountrySettings = (countryCode = null) => {
    const code = countryCode || getCurrentCountryCode();
    return getCountrySettings(code);
};

/**
 * Obtiene el código de país actual desde diferentes fuentes
 * @returns {string} Código de país
 */
export const getCurrentCountryCode = () => {
    // Esta versión está configurada exclusivamente para Colombia
    return 'CO';
};

/**
 * Obtiene tipos de documento de identificación según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de tipos de documento
 */
export const getDocumentTypes = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.type_document_identifications || [];
};

/**
 * Obtiene tipos de factura según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de tipos de factura
 */
export const getInvoiceTypes = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.invoice_types || [];
};

/**
 * Obtiene departamentos/estados según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de departamentos/estados
 */
export const getDepartments = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.departments || [];
};

/**
 * Obtiene tipos de responsabilidad fiscal según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de tipos de responsabilidad
 */
export const getLiabilityTypes = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.type_liabilities || [];
};

/**
 * Obtiene tipos de organización según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de tipos de organización
 */
export const getOrganizationTypes = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.type_organizations || [];
};

/**
 * Obtiene regímenes fiscales según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de regímenes fiscales
 */
export const getRegimeTypes = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.type_regimes || [];
};

/**
 * Obtiene tarifas de IVA según el país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {array} Lista de tarifas de IVA
 */
export const getVatRates = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.vat_rates || [];
};

/**
 * Obtiene la moneda por defecto del país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {string} Código de moneda (ej: 'EUR', 'COP', 'MXN')
 */
export const getDefaultCurrency = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.country?.currency || 'EUR';
};

/**
 * Obtiene el locale del país actual
 * @param {string} countryCode - Código de país (opcional)
 * @returns {string} Locale (ej: 'es-ES', 'es-CO', 'es-MX')
 */
export const getCountryLocale = (countryCode = null) => {
    const config = getCurrentCountrySettings(countryCode);
    return config.country?.locale || 'es-ES';
};

/**
 * Cambia la configuración de país y actualiza todos los settings relacionados
 * @param {string} newCountryCode - Nuevo código de país
 * @returns {object} Nueva configuración aplicada
 */
export const changeCountrySettings = () => {
    // Esta versión está configurada exclusivamente para Colombia
    // No se permite cambiar el país
    console.warn('⚠️ Esta versión del sistema está configurada exclusivamente para Colombia (CO)');
    return getCountrySettings('CO');
};

/**
 * Valida si un documento es válido para el país actual
 * @param {string} documentType - Tipo de documento
 * @param {string} documentNumber - Número de documento
 * @param {string} countryCode - Código de país (opcional)
 * @returns {object} Resultado de validación
 */
export const validateDocument = (documentType, documentNumber, countryCode = null) => {
    const code = countryCode || getCurrentCountryCode();
    const documentTypes = getDocumentTypes(code);
    
    const docType = documentTypes.find(dt => dt.code === documentType);
    if (!docType) {
        return {
            valid: false,
            message: `Tipo de documento ${documentType} no válido para ${code}`
        };
    }

    // Validaciones específicas por país y tipo de documento
    switch (code) {
        case 'ES':
            return validateSpanishDocument(documentType, documentNumber);
        case 'CO':
            return validateColombianDocument(documentType, documentNumber);
        case 'MX':
            return validateMexicanDocument(documentType, documentNumber);
        default:
            return { valid: true, message: 'Documento válido' };
    }
};

/**
 * Validaciones específicas para documentos españoles
 */
const validateSpanishDocument = (type, number) => {
    switch (type) {
        case 'DNI':
            return validateSpanishDNI(number);
        case 'NIE':
            return validateSpanishNIE(number);
        case 'CIF':
            return validateSpanishCIF(number);
        default:
            return { valid: true, message: 'Documento válido' };
    }
};

/**
 * Validaciones específicas para documentos colombianos
 */
const validateColombianDocument = (type, number) => {
    switch (type) {
        case 'CC':
        case 'CE':
            return validateColombianCC(number);
        case 'NIT':
            return validateColombianNIT(number);
        default:
            return { valid: true, message: 'Documento válido' };
    }
};

/**
 * Validaciones específicas para documentos mexicanos
 */
const validateMexicanDocument = (type, number) => {
    switch (type) {
        case 'RFC':
            return validateMexicanRFC(number);
        case 'CURP':
            return validateMexicanCURP(number);
        default:
            return { valid: true, message: 'Documento válido' };
    }
};

// Implementaciones básicas de validación (pueden expandirse)
const validateSpanishDNI = (dni) => {
    const dniRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    if (!dniRegex.test(dni)) {
        return { valid: false, message: 'Formato de DNI inválido' };
    }
    return { valid: true, message: 'DNI válido' };
};

const validateSpanishNIE = (nie) => {
    const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    if (!nieRegex.test(nie)) {
        return { valid: false, message: 'Formato de NIE inválido' };
    }
    return { valid: true, message: 'NIE válido' };
};

const validateSpanishCIF = (cif) => {
    const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/i;
    if (!cifRegex.test(cif)) {
        return { valid: false, message: 'Formato de CIF inválido' };
    }
    return { valid: true, message: 'CIF válido' };
};

const validateColombianCC = (cc) => {
    const ccRegex = /^[0-9]{6,10}$/;
    if (!ccRegex.test(cc)) {
        return { valid: false, message: 'Cédula debe tener entre 6 y 10 dígitos' };
    }
    return { valid: true, message: 'Cédula válida' };
};

const validateColombianNIT = (nit) => {
    const nitRegex = /^[0-9]{9,10}-?[0-9]$/;
    if (!nitRegex.test(nit)) {
        return { valid: false, message: 'Formato de NIT inválido' };
    }
    return { valid: true, message: 'NIT válido' };
};

const validateMexicanRFC = (rfc) => {
    const rfcRegex = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/i;
    if (!rfcRegex.test(rfc)) {
        return { valid: false, message: 'Formato de RFC inválido' };
    }
    return { valid: true, message: 'RFC válido' };
};

const validateMexicanCURP = (curp) => {
    const curpRegex = /^[A-Z]{1}[AEIOU]{1}[A-Z]{2}[0-9]{2}[0-1][0-9][0-3][0-9][HM]{1}[A-Z]{2}[BCDFGHJKLMNPQRSTVWXYZ]{1}[A-Z0-9]{1}[0-9]{1}$/i;
    if (!curpRegex.test(curp)) {
        return { valid: false, message: 'Formato de CURP inválido' };
    }
    return { valid: true, message: 'CURP válido' };
};

/**
 * Obtiene configuración para componentes de UI según el país
 * @param {string} countryCode - Código de país (opcional)
 * @returns {object} Configuración para UI
 */
export const getUIConfiguration = (countryCode = null) => {
    const code = countryCode || getCurrentCountryCode();
    const config = getCurrentCountrySettings(code);
    
    return {
        country: config.country,
        labels: {
            documentTypes: 'Tipos de Documento',
            departments: code === 'ES' ? 'Comunidades Autónomas' : 
                        code === 'CO' ? 'Departamentos' :
                        code === 'MX' ? 'Estados' : 'Departamentos',
            vatName: code === 'ES' ? 'IVA' :
                     code === 'CO' ? 'IVA' :
                     code === 'MX' ? 'IVA' : 'Impuesto',
            regimes: code === 'ES' ? 'Régimen Fiscal' :
                     code === 'CO' ? 'Régimen' :
                     code === 'MX' ? 'Régimen Fiscal' : 'Régimen'
        },
        placeholders: {
            document: code === 'ES' ? 'Ej: 12345678A' :
                     code === 'CO' ? 'Ej: 1234567890' :
                     code === 'MX' ? 'Ej: RFC123456ABC' : 'Número de documento',
            phone: code === 'ES' ? '+34 123 456 789' :
                  code === 'CO' ? '+57 300 123 4567' :
                  code === 'MX' ? '+52 55 1234 5678' : 'Teléfono'
        }
    };
};

/**
 * Reactivamente actualiza todos los componentes cuando cambia el país
 * @param {string} newCountryCode - Nuevo código de país
 */
export const updateComponentsForCountry = () => {
    try {
        // Esta versión está configurada exclusivamente para Colombia
        console.log('🌍 Sistema configurado exclusivamente para Colombia (CO)');

        // Emitir evento personalizado para que los componentes se actualicen
        if (typeof window !== 'undefined') {
            const event = new CustomEvent('countrySettingsChanged', {
                detail: {
                    countryCode: 'CO',
                    config: getCountrySettings('CO')
                }
            });
            window.dispatchEvent(event);
        }

        // Si hay instancia de Vue disponible, emitir evento
        if (window.Vue && window.$root) {
            window.$root.$emit('country-settings-changed', {
                countryCode: 'CO',
                config: getCountrySettings('CO')
            });
        }

        return getCountrySettings('CO');
    } catch (error) {
        console.error('Error actualizando componentes para país:', error);
        throw error;
    }
};

export default {
    getCurrentCountrySettings,
    getCurrentCountryCode,
    getDocumentTypes,
    getInvoiceTypes,
    getDepartments,
    getLiabilityTypes,
    getOrganizationTypes,
    getRegimeTypes,
    getVatRates,
    getDefaultCurrency,
    getCountryLocale,
    changeCountrySettings,
    validateDocument,
    getUIConfiguration,
    updateComponentsForCountry,
    getAvailableCountries,
    isCountrySupported
};
